<template>
  <div class="list__header">
    <div class="list__header-left">
      <div class="list__logo">
        <img src="@/assets/icons/shopping-list.svg" alt="" />
      </div>
      <div class="list__info">
        <h2 class="list__title" v-show="!isEdit">{{ list.name }}</h2>
        <form @submit.prevent="submitListName">
          <input
            class="list__input"
            v-show="isEdit"
            ref="nameInputRef"
            v-model="editName"
            @blur="onEditBlur"
          />
        </form>
        <span class="list__desc">{{ subtitle }}</span>
      </div>
    </div>
    <div class="list__header-right">
      <div class="list__prices">
        <span class="list__total">{{ $t("list.summary") }}</span>
        <span class="list__total-price">{{
          currency(sumsWithCurrencies.PLN)
        }}</span>
        <template v-if="sumsWithCurrencies.GBP">
          <span></span>
          <span class="list__total-price">{{
            currency(sumsWithCurrencies.GBP, "GBP")
          }}</span>
        </template>
        <template v-if="sumsWithCurrencies.USD">
          <span></span>
          <span class="list__total-price">{{
            currency(sumsWithCurrencies.USD, "USD")
          }}</span>
        </template>
        <template v-if="sumsWithCurrencies.EUR">
          <span></span>
          <span class="list__total-price">{{
            currency(sumsWithCurrencies.EUR, "EUR")
          }}</span>
        </template>
        <span class="list__provision">{{ $t("list.provision") }}</span>
        <span class="list__provision-price">{{
          currency(list.commission)
        }}</span>
      </div>
      <button class="list__action" v-focus-click>
        <img src="@/assets/icons/actions.svg" alt="Ikona akcji" />
        <div class="action-menu">
          <ul>
            <li
              v-if="projectInfo.myRoleInProject === Role.Architect"
              @click="onEdit"
              role="button"
              tabindex="0"
            >
              <img src="@/assets/icons/settings.svg" alt="Edytuj listę" />
              {{ $t("list.list_edit") }}
            </li>
            <li
              v-if="projectInfo.myRoleInProject === Role.Architect"
              @click="emit('onDeleteList', { shoppingListId: list.id })"
              role="button"
              tabindex="0"
            >
              <img src="@/assets/icons/delete-v2.svg" alt="Usuń listę" />
              {{ $t("list.list_delete") }}
            </li>
            <li
              @click="emit('onExportList', { shoppingList: list })"
              role="button"
              tabindex="0"
            >
              <img src="@/assets/icons/export-pdf.svg" alt="Eksportuj PDF" />
              Eksportuj PDF
            </li>
          </ul>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { currency } from "@/helpsers/currency.helper";
import { Role } from "@/helpsers/role.helper";
import { useShoppingListStore } from "@/stores/shoppingList.store";
import { useI18n } from "vue-i18n";
import { vFocusClick } from "@/helpsers/focus-click.directive";

const props = defineProps(["list", "projectInfo"]);

const emit = defineEmits(["onDeleteList", "onExportList"]);

const { t } = useI18n();
const subtitle = computed(() => {
  const listOwnerId = props.list.userId;
  const listOwner = props.projectInfo?.members.find(
    (member) => member.id === listOwnerId
  );
  if (listOwner.role == Role.Admin) {
    return t("list.admins_list");
  }
  if (listOwner.role == Role.Architect) {
    return t("list.architects_list");
  }
  return t("list.clients_list");
});

const isEdit = ref(false);
const nameInputRef = ref();
const editName = ref("");
function onEdit() {
  isEdit.value = true;
  editName.value = props.list.name;
  setTimeout(() => {
    nameInputRef.value?.focus();
  }, 0);
}
function onEditBlur() {
  isEdit.value = false;
  editName.value = "";
}

const store = useShoppingListStore();

async function submitListName() {
  const { isSuccess } = await store.editListName({
    shoppingListId: props.list.id,
    name: editName.value,
  });
  if (isSuccess) {
    isEdit.value = false;
    editName.value = "";
  }
}

/** @type {import('vue').ComputedRef<{PLN: number; EUR: number; USD: number; GBP: number}>} */
const sumsWithCurrencies = computed(() => {
  const allProducts = [];
  for (const room of props.list.shoppingListRooms) {
    for (const product of room.shoppingListRoomProducts) {
      allProducts.push(product);
    }
  }
  const sumMap = allProducts.reduce(
    (acc, value) => {
      acc[value.currency] += value.price * value.amount;
      return acc;
    },
    {
      PLN: 0,
      EUR: 0,
      USD: 0,
      GBP: 0,
    }
  );
  return sumMap;
});
</script>

<style scoped lang="scss">
@use "@/css/shopping-list.scss";
@use "@/css/shopping-list-header.scss";
</style>
